// frontend/src/admin/v2/hooks/useDashboardSummary.js
import { useEffect, useState, useCallback, useRef } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_BASE_URL
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY
const HEADERS = ADMIN_KEY ? { 'X-Admin-Key': ADMIN_KEY } : {}
const POLL_MS = 30_000

/**
 * useDashboardSummary — polls /api/admin/dashboard/summary every 30s.
 *
 * AbortController cancels inflight on unmount or rapid refetch so a slow
 * response doesn't commit stale state. Re-checks `inflightRef.current ===
 * controller` before setData() in case the user navigates away.
 *
 * Returns { data, loading, error, errorTitle, refetch }.
 *
 * 🔴 Welle 3 PR 4 (§4.1): `error` ist der Nutzersatz, `errorTitle` traegt das
 * Entwicklerdetail (den Env-Namen) fuer den Tooltip. Der Env-Name steht nie
 * mehr im sichtbaren Text.
 */
export function useDashboardSummary() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errorTitle, setErrorTitle] = useState(null)
  const inflightRef = useRef(null)

  const fetchSummary = useCallback(async () => {
    if (inflightRef.current) inflightRef.current.abort()
    const c = new AbortController()
    inflightRef.current = c
    try {
      const r = await axios.get(`${API}/api/admin/dashboard/summary`, {
        headers: HEADERS,
        signal: c.signal,
      })
      if (inflightRef.current !== c) return
      setData(r.data)
      setError(null)
      setErrorTitle(null)
    } catch (e) {
      if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return
      // 403 = admin key missing/wrong; show as error so the UI can prompt.
      const isKey = e.response?.status === 403
      setError(isKey ? 'Admin data is unavailable in this build.' : (e.message || 'Failed to load'))
      setErrorTitle(isKey ? 'REACT_APP_ADMIN_KEY' : null)
    } finally {
      if (inflightRef.current === c) {
        setLoading(false)
        inflightRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    fetchSummary()
    const id = setInterval(fetchSummary, POLL_MS)
    return () => {
      clearInterval(id)
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
    }
  }, [fetchSummary])

  return { data, loading, error, errorTitle, refetch: fetchSummary }
}
